/**
 * 墙面平坦场模型 —— 逐行对拍 climb-core/src/lib.rs:186-416
 * 分块中位数 + MAD σ + 3×3 平滑 + 双线性采样；净化变体用亮部统计口径。
 */
import { rgbToLab } from "./color";
import type { HoldKind } from "./types";
import { HoldKind as HK } from "./types";
import type { CompStats } from "./conn";

export const TILE = 160;

export interface WallField {
  tw: number;
  th: number;
  l: Float32Array;
  a: Float32Array;
  b: Float32Array;
  sigma: Float32Array;
}

/** 中位数（排序后取上中位，与 Rust v[v.len()/2] 一致） */
export function median3(v: number[]): number {
  if (v.length === 0) return 0;
  v.sort((x, y) => x - y);
  return v[v.length >> 1];
}

/** 3×3 均值平滑（边缘钳制复制） */
export function smooth3x3(src: Float32Array, tw: number, th: number): Float32Array {
  const out = new Float32Array(src.length);
  for (let y = 0; y < th; y++) {
    for (let x = 0; x < tw; x++) {
      let acc = 0;
      let cnt = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const yy = Math.min(Math.max(y + dy, 0), th - 1);
          const xx = Math.min(Math.max(x + dx, 0), tw - 1);
          acc += src[yy * tw + xx];
          cnt++;
        }
      }
      out[y * tw + x] = acc / cnt;
    }
  }
  return out;
}

/** 分块估计墙面颜色与局部噪声 σ，并 3×3 平滑（对拍 build_wall_field） */
export function buildWallField(
  lab: Float32Array,
  sat: Float32Array,
  w: number,
  h: number,
): WallField {
  const tw = Math.ceil(w / TILE);
  const th = Math.ceil(h / TILE);
  const nt = tw * th;
  const fl = new Float64Array(nt);
  const fa = new Float64Array(nt);
  const fb = new Float64Array(nt);
  const fsig = new Float64Array(nt);

  const gl: number[] = [];
  const ga: number[] = [];
  const gb: number[] = [];
  for (let i = 0; i < sat.length; i += 3) {
    if (sat[i] < 0.2) {
      gl.push(lab[i * 3]);
      ga.push(lab[i * 3 + 1]);
      gb.push(lab[i * 3 + 2]);
    }
  }
  const gml = median3(gl);
  const gma = median3(ga);
  const gmb = median3(gb);
  const gd = gl.map((l, i) =>
    Math.sqrt(
      (l - gml) ** 2 + (ga[i] - gma) ** 2 + (gb[i] - gmb) ** 2,
    ),
  );
  const gsig = Math.min(Math.max(1.4826 * median3(gd), 2.5), 14.0);

  const samples: number[][] = Array.from({ length: nt }, () => []);
  let i = 0;
  for (let y = 0; y < h; y++) {
    const ty = Math.min(Math.floor(y / TILE), th - 1);
    for (let x = 0; x < w; x++) {
      if (sat[i] < 0.2 && (x + y) % 2 === 0) {
        const tx = Math.min(Math.floor(x / TILE), tw - 1);
        samples[ty * tw + tx].push(i);
      }
      i++;
    }
  }
  for (let t = 0; t < nt; t++) {
    if (samples[t].length < 40) {
      fl[t] = gml;
      fa[t] = gma;
      fb[t] = gmb;
      fsig[t] = gsig;
      continue;
    }
    const cl = samples[t].map((j) => lab[j * 3]);
    const ca = samples[t].map((j) => lab[j * 3 + 1]);
    const cb = samples[t].map((j) => lab[j * 3 + 2]);
    const ml = median3(cl);
    const ma = median3(ca);
    const mb = median3(cb);
    const cds = samples[t].map((j) =>
      Math.sqrt(
        (lab[j * 3] - ml) ** 2 +
          (lab[j * 3 + 1] - ma) ** 2 +
          (lab[j * 3 + 2] - mb) ** 2,
      ),
    );
    fl[t] = ml;
    fa[t] = ma;
    fb[t] = mb;
    fsig[t] = Math.min(Math.max(1.4826 * median3(cds), 2.5), 14.0);
  }
  return {
    tw,
    th,
    l: Float32Array.from(smooth3x3(Float32Array.from(fl), tw, th)),
    a: Float32Array.from(smooth3x3(Float32Array.from(fa), tw, th)),
    b: Float32Array.from(smooth3x3(Float32Array.from(fb), tw, th)),
    sigma: Float32Array.from(smooth3x3(Float32Array.from(fsig), tw, th)),
  };
}

/**
 * 墙面净化用的墙面场变体（对拍 build_fill_field）：块内排除 exclude>0 像素，
 * 取低饱和墙面像素 P90 明度 + 亮部像素中位数色；样本不足先 3×3 邻块联合，再回退全局。
 */
export function buildFillField(
  raw: Uint8ClampedArray,
  sat: Float32Array,
  exclude: Uint8Array,
  w: number,
  h: number,
): WallField {
  const tw = Math.ceil(w / TILE);
  const th = Math.ceil(h / TILE);
  const nt = tw * th;

  const samples: number[][] = Array.from({ length: nt }, () => []);
  let i = 0;
  for (let y = 0; y < h; y++) {
    const ty = Math.min(Math.floor(y / TILE), th - 1);
    for (let x = 0; x < w; x++) {
      if (exclude[i] === 0 && sat[i] < 0.2 && (x + y) % 2 === 0) {
        const tx = Math.min(Math.floor(x / TILE), tw - 1);
        samples[ty * tw + tx].push(i);
      }
      i++;
    }
  }

  const brightStats = (idx: number[]): [number, number, number] | null => {
    if (idx.length < 40) return null;
    const pts: Array<[number, number, number]> = idx.map((j) => {
      const [l, a, b] = rgbToLab(raw[j * 4], raw[j * 4 + 1], raw[j * 4 + 2]);
      return [l, a, b];
    });
    pts.sort((x, y) => x[0] - y[0]);
    const p90 = pts[Math.min(Math.floor((pts.length * 9) / 10), pts.length - 1)][0];
    const ba = pts.filter((p) => p[0] >= p90).map((p) => p[1]);
    const bb = pts.filter((p) => p[0] >= p90).map((p) => p[2]);
    return [p90, median3(ba), median3(bb)];
  };

  const all: number[] = samples.flat();
  const global = brightStats(all) ?? [50, 0, 0];

  const neighStats = (t: number): [number, number, number] | null => {
    const tx = t % tw;
    const ty = Math.floor(t / tw);
    const idx: number[] = [];
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = tx + dx;
        const ny = ty + dy;
        if (nx >= 0 && ny >= 0 && nx < tw && ny < th) {
          idx.push(...samples[ny * tw + nx]);
        }
      }
    }
    return brightStats(idx);
  };

  const fl = new Float64Array(nt);
  const fa = new Float64Array(nt);
  const fb = new Float64Array(nt);
  for (let t = 0; t < nt; t++) {
    const [l, a, b] = brightStats(samples[t]) ?? neighStats(t) ?? global;
    fl[t] = l;
    fa[t] = a;
    fb[t] = b;
  }
  return {
    tw,
    th,
    l: Float32Array.from(smooth3x3(Float32Array.from(fl), tw, th)),
    a: Float32Array.from(smooth3x3(Float32Array.from(fa), tw, th)),
    b: Float32Array.from(smooth3x3(Float32Array.from(fb), tw, th)),
    sigma: new Float32Array(nt),
  };
}

/** 双线性取墙面参考色与 σ（tile 中心插值）。对拍 lib.rs:381-401 */
export function wallRef(
  f: WallField,
  x: number,
  y: number,
): { lab: [number, number, number]; sigma: number } {
  const fx = (x + 0.5) / TILE - 0.5;
  const fy = (y + 0.5) / TILE - 0.5;
  const cx = Math.max(fx, 0);
  const cy = Math.max(fy, 0);
  const tx0 = Math.min(Math.floor(cx), f.tw - 1);
  const ty0 = Math.min(Math.floor(cy), f.th - 1);
  const tx1 = Math.min(tx0 + 1, f.tw - 1);
  const ty1 = Math.min(ty0 + 1, f.th - 1);
  const rx = Math.min(Math.max(cx - tx0, 0), 1);
  const ry = Math.min(Math.max(cy - ty0, 0), 1);
  const idx = (tx: number, ty: number) => ty * f.tw + tx;
  const lerp = (v00: number, v10: number, v01: number, v11: number) =>
    (v00 * (1 - rx) + v10 * rx) * (1 - ry) + (v01 * (1 - rx) + v11 * rx) * ry;
  return {
    lab: [
      lerp(f.l[idx(tx0, ty0)], f.l[idx(tx1, ty0)], f.l[idx(tx0, ty1)], f.l[idx(tx1, ty1)]),
      lerp(f.a[idx(tx0, ty0)], f.a[idx(tx1, ty0)], f.a[idx(tx0, ty1)], f.a[idx(tx1, ty1)]),
      lerp(f.b[idx(tx0, ty0)], f.b[idx(tx1, ty0)], f.b[idx(tx0, ty1)], f.b[idx(tx1, ty1)]),
    ],
    sigma: lerp(
      f.sigma[idx(tx0, ty0)],
      f.sigma[idx(tx1, ty0)],
      f.sigma[idx(tx0, ty1)],
      f.sigma[idx(tx1, ty1)],
    ),
  };
}

/** 裁剪窗口的局部墙面参考色与噪声 σ（低饱和像素中位数）。对拍 lib.rs:1094-1119 */
export function localWallRef(
  lab: Float32Array,
  sat: Float32Array,
  w: number,
  x0: number,
  y0: number,
  cw: number,
  ch: number,
): { med: [number, number, number]; sigma: number } | null {
  const cl: number[] = [];
  const ca: number[] = [];
  const cb: number[] = [];
  for (let dy = 0; dy < ch; dy++) {
    for (let dx = 0; dx < cw; dx++) {
      const i = (y0 + dy) * w + x0 + dx;
      if (sat[i] < 0.2) {
        cl.push(lab[i * 3]);
        ca.push(lab[i * 3 + 1]);
        cb.push(lab[i * 3 + 2]);
      }
    }
  }
  if (cl.length < 20) return null;
  const ml = median3(cl);
  const ma = median3(ca);
  const mb = median3(cb);
  const ds = cl.map((l, i2) =>
    Math.sqrt((l - ml) ** 2 + (ca[i2] - ma) ** 2 + (cb[i2] - mb) ** 2),
  );
  const sigma = Math.min(Math.max(1.4826 * median3(ds), 2.0), 14.0);
  return { med: [ml, ma, mb], sigma };
}

/** 颜色确认分类（对拍 classify_stats） */
export function classifyStats(s: CompStats, minColoredFrac: number): HoldKind {
  const [, purity] = s.modeHue();
  if (s.coloredFrac() >= minColoredFrac && purity >= 0.45) return HK.Colored;
  if (s.valMean() < 0.25) return HK.Black;
  if (s.valMean() > 0.7 && s.satMean() < 0.3) return HK.White;
  return HK.Gray;
}
